/**
 * GPS pseudo-anchors for comparisons that have no race checkpoints.
 *
 * Why this exists
 * ---------------
 * The gap chart draws a reconstructed line when checkpoints are available:
 * a stop-free moving base, stops added back as clean steps, and a drift
 * correction pinning the curve to the measured arrival gap at each checkpoint.
 * Without checkpoints it fell back to the raw `elapsed_b - elapsed_a`, which
 * spikes wherever either runner stopped — each stop lands on its own GPS track
 * and the two never cancel on courses of different recorded length.
 *
 * A DTW-aligned pair already asserts "these two points are the same place", so
 * it can stand in for a checkpoint. It is a weaker claim, and deliberately so:
 *
 *   A real checkpoint carries an EXTERNAL truth — the organiser's lat/lon — and
 *   B's index is re-derived from it, so it can correct the alignment where the
 *   alignment was wrong. A pseudo-anchor comes from the same alignment, so it
 *   cannot. What it can do is stop drift accumulating across well-matched
 *   stretches and keep stop time out of the base line.
 *
 * Anchors are therefore only taken where the pairing is worth believing, and
 * the guard rails below throw away the places where it is not.
 */

// A pairing this far apart is not the same place; ~2 chunk lengths.
const MAX_PAIR_DIST_M = 120;

// Leave room between anchors so the drift correction interpolates rather than
// tracking every wobble in the alignment.
const MIN_ANCHOR_SPACING_M = 1500;

// Course positions closer together than this on the ground, but further apart
// than REVISIT_MIN_SEPARATION_M along the course, mark a revisited area.
const REVISIT_RADIUS_M = 60;
const REVISIT_MIN_SEPARATION_M = 3000;

// How far an anchor's gap RATE may deviate from the typical rate before it is
// treated as an alignment artefact. Expressed in robust deviations (MAD), so it
// calibrates itself to the pair being compared rather than assuming a scale.
const OUTLIER_MAD_MULTIPLE = 6;
// Floor for the MAD, so a very steady pair does not get a near-zero tolerance
// and reject every normal fluctuation. Seconds of gap per metre of course.
const MIN_RATE_TOLERANCE_S_PER_M = 0.08;

const toRad = (d) => (d * Math.PI) / 180;

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Course distances that sit inside a revisited area — a loop closing on itself,
 * or the two legs of an out-and-back.
 *
 * This is the guard that matters most. On such a course one place belongs to two
 * course positions, so a tight GPS pairing there proves nothing: DTW may have
 * matched the outbound leg to the return leg, and an anchor would lock the curve
 * onto that mistake with full confidence. Anchoring is simply refused there and
 * the correction interpolates across instead.
 *
 * Uses A's own track (altitude_a is irrelevant; lat/lon come from the caller),
 * bucketed into a grid so this stays linear-ish rather than O(n^2).
 */
export function findRevisitedRanges(points) {
  const cell = REVISIT_RADIUS_M / 111000; // degrees, near enough at any latitude
  const grid = new Map();
  const key = (la, lo) => `${Math.round(la / cell)}:${Math.round(lo / cell)}`;

  points.forEach((p, idx) => {
    if (p.lat == null || p.lon == null) return;
    const k = key(p.lat, p.lon);
    if (!grid.has(k)) grid.set(k, []);
    grid.get(k).push(idx);
  });

  const revisited = new Set();
  for (const idxs of grid.values()) {
    if (idxs.length < 2) continue;
    for (let a = 0; a < idxs.length; a++) {
      for (let b = a + 1; b < idxs.length; b++) {
        const pa = points[idxs[a]];
        const pb = points[idxs[b]];
        if (Math.abs(pa.distance_m - pb.distance_m) < REVISIT_MIN_SEPARATION_M) continue;
        if (haversine(pa.lat, pa.lon, pb.lat, pb.lon) > REVISIT_RADIUS_M) continue;
        revisited.add(idxs[a]);
        revisited.add(idxs[b]);
      }
    }
  }
  return revisited;
}

/**
 * Choose anchors from the resampled rows.
 *
 * `rows` need `course_distance_m` and `time_delta_s`; `pair_dist_m` and the
 * moving columns are used when present. Rows stored before those existed still
 * work — see the notes on each guard.
 *
 * Returns [{ distance_m, gap_at_cp_s, stop_a_s: 0, stop_b_s: 0 }], the shape the
 * chart's checkpoint path already consumes, so nothing downstream changes.
 */
export function buildGpsAnchors(rows, trackPoints = null) {
  if (!rows || rows.length < 4) return [];

  const hasPairDist = rows.some((r) => r.pair_dist_m != null);
  const hasMoving = rows[0]?.moving_elapsed_a_s != null && rows[0]?.moving_elapsed_b_s != null;

  // Loop / out-and-back guard. Needs coordinates; when the caller has none
  // (older stored comparisons carry no per-row lat/lon) this yields an empty
  // set and the remaining guards carry the load.
  const revisited = trackPoints ? findRevisitedRanges(trackPoints) : new Set();

  const candidates = [];
  rows.forEach((row, idx) => {
    // Believe only pairings that are actually co-located. Without pair_dist_m
    // (older rows) this filter cannot run, and the outlier guard below is what
    // catches bad anchors instead.
    if (hasPairDist && (row.pair_dist_m == null || row.pair_dist_m > MAX_PAIR_DIST_M)) return;
    if (revisited.has(idx)) return;

    // Never anchor inside a stop. DTW smears one runner's many stopped chunks
    // onto the other's few, and the path lookup resolves that many-to-one by
    // keeping the LAST B index — a systematic bias. Anchoring there would bake
    // it in; stepping over those stretches avoids it.
    if (hasMoving && idx > 0) {
      const prev = rows[idx - 1];
      const movedA = row.moving_elapsed_a_s - prev.moving_elapsed_a_s;
      const movedB = row.moving_elapsed_b_s - prev.moving_elapsed_b_s;
      const elapsedA = row.elapsed_a_s - prev.elapsed_a_s;
      const elapsedB = row.elapsed_b_s - prev.elapsed_b_s;
      if (movedA <= 0 || movedB <= 0) return;
      if (elapsedA - movedA > 5 || elapsedB - movedB > 5) return;   // someone paused here
    }

    candidates.push({ idx, distance_m: row.course_distance_m, gap: row.time_delta_s });
  });

  if (!candidates.length) return [];

  // Space them out, so the correction interpolates a trend instead of chasing
  // every wobble in the alignment.
  const spaced = [];
  let lastD = -Infinity;
  for (const c of candidates) {
    if (c.distance_m - lastD < MIN_ANCHOR_SPACING_M) continue;
    spaced.push(c);
    lastD = c.distance_m;
  }
  if (spaced.length < 2) return [];

  // Outlier guard, on the RATE of gap change rather than its magnitude.
  //
  // The gap is cumulative: when one runner pulls away it grows to thousands of
  // seconds, and every later anchor differs hugely from the earlier ones. An
  // earlier version compared those absolute values and threw away almost every
  // anchor — it was rejecting the signal itself. What actually marks an
  // alignment slip is the gap moving far faster over a stretch than this pair
  // moves anywhere else, so the test is against the pair's own typical rate,
  // measured robustly (median + MAD) so a few bad anchors cannot set the scale.
  const rates = [];
  for (let i = 1; i < spaced.length; i++) {
    const span = Math.max(spaced[i].distance_m - spaced[i - 1].distance_m, 1);
    rates.push((spaced[i].gap - spaced[i - 1].gap) / span);
  }
  const median = (xs) => {
    if (!xs.length) return 0;
    const v = [...xs].sort((a, b) => a - b);
    const m = Math.floor(v.length / 2);
    return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
  };
  const medRate = median(rates);
  const mad = Math.max(median(rates.map((r) => Math.abs(r - medRate))),
                       MIN_RATE_TOLERANCE_S_PER_M);
  const maxDeviation = OUTLIER_MAD_MULTIPLE * mad;

  const kept = [spaced[0]];
  for (let i = 1; i < spaced.length; i++) {
    const prev = kept[kept.length - 1];
    const span = Math.max(spaced[i].distance_m - prev.distance_m, 1);
    const rate = (spaced[i].gap - prev.gap) / span;
    if (Math.abs(rate - medRate) > maxDeviation) continue;
    kept.push(spaced[i]);
  }
  if (kept.length < 2) return [];

  return kept.map((c) => ({
    distance_m: c.distance_m,
    gap_at_cp_s: c.gap,
    stop_a_s: 0,
    stop_b_s: 0,
    synthetic: true,
  }));
}

/**
 * Combine hard checkpoint corrections with soft GPS-derived shape.
 *
 * The checkpointed gap curve used to interpolate its correction linearly
 * between checkpoints. With a handful of aid stations over 80 km that turns
 * every leg into a straight ramp: the correction is spread evenly along the
 * leg even though whatever it is absorbing — a scattered stop, a patch of
 * alignment drift — happened at one spot. The result reads as artificial
 * because it is: the structure was averaged away.
 *
 * GPS anchors supply that missing structure. They are not trusted for their
 * VALUES here — those carry the DTW drift the checkpoints exist to correct —
 * only for their SHAPE. Within each checkpoint interval the synthetic curve is
 * shifted by an affine ramp chosen so both ends land exactly on the checkpoint
 * corrections. Checkpoints stay authoritative to the second; the wiggle between
 * them comes from the GPS.
 *
 * @param hard  [{distance_m, correction}] from real checkpoints (sorted)
 * @param soft  [{distance_m, correction}] from GPS anchors (sorted)
 * @returns (distance_m) => correction seconds
 */
export function buildCombinedCorrection(hard, soft) {
  const lerpOver = (pts) => (d) => {
    if (!pts.length) return 0;
    if (d <= pts[0].distance_m) return pts[0].correction;
    const last = pts[pts.length - 1];
    if (d >= last.distance_m) return last.correction;
    const ri = pts.findIndex((p) => p.distance_m >= d);
    const l = pts[ri - 1];
    const r = pts[ri];
    const t = (d - l.distance_m) / (r.distance_m - l.distance_m || 1);
    return l.correction + t * (r.correction - l.correction);
  };

  if (!hard?.length) return lerpOver(soft || []);
  if (!soft?.length) return lerpOver(hard);

  const softAt = lerpOver(soft);

  // Offset needed at each checkpoint so the combined curve hits it exactly.
  const offsets = hard.map((h) => ({
    distance_m: h.distance_m,
    offset: h.correction - softAt(h.distance_m),
  }));
  const offsetAt = (d) => {
    if (d <= offsets[0].distance_m) return offsets[0].offset;
    const last = offsets[offsets.length - 1];
    if (d >= last.distance_m) return last.offset;
    const ri = offsets.findIndex((o) => o.distance_m >= d);
    const l = offsets[ri - 1];
    const r = offsets[ri];
    const t = (d - l.distance_m) / (r.distance_m - l.distance_m || 1);
    return l.offset + t * (r.offset - l.offset);
  };

  return (d) => softAt(d) + offsetAt(d);
}
